import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GiFairyWings } from 'react-icons/gi';
import { FaDonate, FaUser, FaPhoneAlt, FaIdCard, FaMapMarkerAlt, FaMoneyBillWave, FaVenusMars, FaCalendarAlt, FaCity, FaMap, FaEnvelope, FaBriefcase } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import './DonateNidhi.css';
import statesDistrictsData from '../../JsonData/india-states-districts-latest.json';

const EF_TYPE = 'Encouragement Fund (प्रोत्साहन निधि)';

const SearchableSelect = ({ name, value, options, onChange, placeholder, icon: Icon, required, readOnly, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => { setSearchTerm(value); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        if (searchTerm && !options.includes(searchTerm)) {
          setSearchTerm('');
          onChange({ target: { name, value: '' } });
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchTerm, options, name, onChange]);

  const filtered = options.filter(o => o.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    onChange({ target: { name, value: e.target.value } });
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) {
        const selected = filtered[highlightedIndex];
        setSearchTerm(selected);
        onChange({ target: { name, value: selected } });
        setIsOpen(false);
      }
    }
  };

  useEffect(() => { setHighlightedIndex(0); }, [searchTerm]);

  return (
    <div className="protsahan-nidhi-donate-now-page-searchable-wrap" ref={wrapperRef}>
      {Icon && <Icon className="protsahan-nidhi-donate-now-page-input-icon" />}
      <input 
        type="text" name={name} value={searchTerm} onChange={handleInputChange} onKeyDown={handleKeyDown}
        onFocus={() => { if (!readOnly && !disabled) setIsOpen(true); }} 
        placeholder={placeholder} className="protsahan-nidhi-donate-now-page-input" 
        required={required} readOnly={readOnly} disabled={disabled} autoComplete="off" 
        style={{ backgroundColor: disabled || readOnly ? '#f1f5f9' : '#fff', cursor: disabled || readOnly ? 'not-allowed' : 'text' }}
      />
      {isOpen && !readOnly && !disabled && (
        <ul className="protsahan-nidhi-donate-now-page-searchable-dropdown-list">
          {filtered.length > 0 ? filtered.map((opt, idx) => (
            <li 
              key={opt} className={idx === highlightedIndex ? 'highlighted' : ''}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                setSearchTerm(opt); onChange({ target: { name, value: opt } }); setIsOpen(false); 
              }}
            >
              {opt}
            </li>
          )) : <li className="protsahan-nidhi-donate-now-page-searchable-no-data">No matching option</li>}
        </ul>
      )}
    </div>
  );
};

const DonateNidhi = () => {
  const [formData, setFormData] = useState({
    mobileNo: '', name: '', fatherName: '', occupation: '', email: '', gender: '', birthDate: '', aadhaarNo: '', panNo: '', currentAddress: '', permanentAddress: '', isSameAddress: false, state: '', district: '', donationType: EF_TYPE, amount: '5000'
  });

  const [isExisting, setIsExisting] = useState(false);
  const [fetchedData, setFetchedData] = useState({});
  const [agreed, setAgreed] = useState(false); 

  const allStates = useMemo(() => statesDistrictsData.map((item) => item.state).filter(Boolean).sort(), []);
  const availableDistricts = useMemo(() => {
    if (!formData.state) return [];
    const found = statesDistrictsData.find((item) => item.state === formData.state);
    return found?.districts?.sort() || [];
  }, [formData.state]);

  const maskAadhaar = (val) => val && val.length > 4 ? 'X'.repeat(val.length - 4) + val.slice(-4) : val;
  const maskPan = (val) => val && val.length > 1 ? val.charAt(0) + 'X'.repeat(val.length - 1) : val;

  const handleMobileChange = (e) => {
    let val = e.target.value.replace(/\D/g, ''); 
    if (val.length > 0 && !/^[6-9]/.test(val)) val = val.substring(1); 

    if (val.length < 10 && isExisting) {
      setIsExisting(false);
      setFetchedData({});
      setFormData({
        mobileNo: val, name: '', fatherName: '', occupation: '', email: '', gender: '', birthDate: '', aadhaarNo: '', panNo: '', currentAddress: '', permanentAddress: '', isSameAddress: false, state: '', district: '', donationType: EF_TYPE, amount: '5000'
      });
    } else {
      setFormData(prev => ({ ...prev, mobileNo: val }));
    }
  };

  useEffect(() => {
    if (formData.mobileNo.length !== 10 || isExisting) return;
    const timer = setTimeout(() => {}, 8000);
    axiosInstance.get(`/api/donors/public/phone/${formData.mobileNo}`)
      .then(res => {
        const data = res.data;
        if (data.success && data.data) {
          const d = data.data;
          setIsExisting(true);
          setFetchedData(d);
          setFormData(prev => ({
            ...prev,
            name: d.name || prev.name,
            fatherName: d.fatherName || prev.fatherName,
            occupation: d.occupation || prev.occupation,
            email: d.email || prev.email,
            gender: d.gender || prev.gender,
            birthDate: d.birthDate ? d.birthDate.split('T')[0] : prev.birthDate,
            aadhaarNo: d.aadharNo ? maskAadhaar(d.aadharNo) : prev.aadhaarNo,
            panNo: d.panNo ? maskPan(d.panNo) : prev.panNo,
            currentAddress: d.currentAddress || prev.currentAddress,
            permanentAddress: d.permanentAddress || prev.permanentAddress,
            isSameAddress: !!d.isSameAddress,
            state: d.state || prev.state,
            district: d.district || prev.district
          }));
        }
      })
      .catch(() => {})
      .finally(() => clearTimeout(timer));
    return () => clearTimeout(timer);
  }, [formData.mobileNo, isExisting]);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'name') value = value.replace(/[^a-zA-Z\s]/g, ''); 
    if (name === 'fatherName') value = value.replace(/[^a-zA-Z\s]/g, ''); 
    if (name === 'aadhaarNo') value = value.replace(/\D/g, ''); 
    if (name === 'panNo') value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(); 

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCurrentAddressChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      currentAddress: val,
      permanentAddress: prev.isSameAddress ? val : prev.permanentAddress
    }));
  };

  const handleSameAddressCheckboxChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      isSameAddress: checked,
      permanentAddress: checked ? prev.currentAddress : ''
    }));
  };

  const handleStateChange = (e) => {
    setFormData(prev => ({ ...prev, state: e.target.value, district: '' })); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobileNo.length !== 10) return Swal.fire('Error', 'Mobile number must be 10 digits.', 'error');
    if (!formData.name) return Swal.fire('Error', 'Full name is required.', 'error');
    if (!formData.fatherName) return Swal.fire('Error', "Father's name is required.", 'error');
    if (!formData.occupation) return Swal.fire('Error', 'Occupation is required.', 'error');
    if (!formData.email) return Swal.fire('Error', 'Email address is required.', 'error');
    if (!formData.gender) return Swal.fire('Error', 'Gender is required.', 'error');
    if (!formData.birthDate) return Swal.fire('Error', 'Birth Date is required.', 'error');
    
    // Compulsory validation checks for Aadhaar and PAN Card
    if (!formData.aadhaarNo || formData.aadhaarNo.length !== 12) {
      return Swal.fire('Error', 'Aadhaar Card Number must be exactly 12 digits.', 'error');
    }
    if (!formData.panNo || formData.panNo.length !== 10) {
      return Swal.fire('Error', 'PAN Card Number must be exactly 10 characters.', 'error');
    }

    if (!formData.currentAddress) return Swal.fire('Error', 'Current address is required.', 'error');
    if (!formData.permanentAddress) return Swal.fire('Error', 'Permanent address is required.', 'error');

    // Only validate agreement checkbox if user is NOT verified (not existing in the database)
    if (!isExisting && !agreed) {
      return Swal.fire('Warning', 'कृपया प्रोत्साहन निधि सदस्यता घोषणा पत्र से सहमत होने के लिए चेकबॉक्स पर टिक करें।', 'warning');
    }

    const payload = {
      ...formData,
      aadharNo: (isExisting && fetchedData.aadharNo) ? fetchedData.aadharNo : formData.aadhaarNo,
      panNo: (isExisting && fetchedData.panNo) ? fetchedData.panNo : formData.panNo
    };

    try {
      Swal.fire({ title: 'Processing...', text: 'Connecting to Payment Gateway', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
      const res = await axiosInstance.post('/api/payment/initiate', payload);
      const resData = res.data;
      if (resData && resData.success && resData.redirectURI) {
        window.location.href = resData.redirectURI;
      } else {
        Swal.fire('Error', resData?.message || 'Payment initiation failed', 'error');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Server error. Please try again.';
      Swal.fire('Error', msg, 'error');
    }
  };

  return (
    <>
      <Helmet><title>Protsahan Nidhi Donate | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="Your contribution helps us create better educational opportunities. Donate now to support Rajput Shiksha Kosh."
        />
      </Helmet>
    <div className="protsahan-nidhi-donate-now-page-wrapper">
      <div className="protsahan-nidhi-donate-now-page-container">
        <div className="protsahan-nidhi-donate-now-page-header">
          <div className="protsahan-nidhi-donate-now-page-header-icon-wrap"><FaDonate className="protsahan-nidhi-donate-now-page-header-icon" /></div>
          <h2 className="protsahan-nidhi-donate-now-page-title">राजपूत शिक्षा प्रोत्साहन निधि</h2>
          <p className="protsahan-nidhi-donate-now-page-subtitle">आपका योगदान हमारे समाज के बच्चों के लिए बेहतर शैक्षणिक अवसर प्रदान करने में मदद करता है।</p>
          <div className="protsahan-nidhi-donate-now-page-divider"><span className="protsahan-nidhi-donate-now-page-divider-line"></span><GiFairyWings className="protsahan-nidhi-donate-now-page-divider-icon" /><span className="protsahan-nidhi-donate-now-page-divider-line"></span></div>
        </div>

        <div className="protsahan-nidhi-donate-now-page-form-card">
          <form onSubmit={handleSubmit}>
            <div className="protsahan-nidhi-donate-now-page-grid">
              
              <div className="protsahan-nidhi-donate-now-page-field protsahan-nidhi-donate-now-page-full-width">
                <label className="protsahan-nidhi-donate-now-page-label">Enter Contact Number <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaPhoneAlt className="protsahan-nidhi-donate-now-page-input-icon" />
                  <span className="protsahan-nidhi-donate-now-page-phone-prefix">+91</span>
                  <input type="tel" name="mobileNo" value={formData.mobileNo} onChange={handleMobileChange} maxLength="10" placeholder="Enter 10-digit mobile number" className="protsahan-nidhi-donate-now-page-input protsahan-nidhi-donate-now-page-phone-input" required />
                  {isExisting && <span className="protsahan-nidhi-donate-now-page-verified-badge">Verified</span>}
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Full Name <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaUser className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter full name" className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.name} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Father's Name <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaUser className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} placeholder="Enter father's name" className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.fatherName} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Occupation <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaBriefcase className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="occupation" value={formData.occupation} onChange={handleChange} placeholder="Enter your occupation" className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.occupation} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Email ID <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaEnvelope className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter email address" className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.email} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Donation Type</label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaDonate className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="donationType" value={formData.donationType} readOnly className="protsahan-nidhi-donate-now-page-input" />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Donation Amount (₹)</label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaMoneyBillWave className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="amount" value={formData.amount} readOnly className="protsahan-nidhi-donate-now-page-input" style={{ fontWeight: '700', color: '#F37936' }} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Gender <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaVenusMars className="protsahan-nidhi-donate-now-page-input-icon" />
                  <select name="gender" value={formData.gender} onChange={handleChange} className="protsahan-nidhi-donate-now-page-select protsahan-nidhi-donate-now-page-has-icon" required disabled={isExisting && !!fetchedData.gender}>
                    <option value="">Select Gender</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Birth Date <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaCalendarAlt className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="date" name="birthDate" value={formData.birthDate} onChange={handleChange} className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.birthDate} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">Aadhaar Card Number <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaIdCard className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="aadhaarNo" value={formData.aadhaarNo} onChange={handleChange} maxLength="12" placeholder="Enter Aadhaar number" className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.aadharNo} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">PAN Card Number <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap">
                  <FaIdCard className="protsahan-nidhi-donate-now-page-input-icon" />
                  <input type="text" name="panNo" value={formData.panNo} onChange={handleChange} maxLength="10" placeholder="Enter PAN number" className="protsahan-nidhi-donate-now-page-input" required readOnly={isExisting && !!fetchedData.panNo} />
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">State <span style={{ color: 'red' }}>*</span></label>
                <SearchableSelect name="state" value={formData.state} options={allStates} onChange={handleStateChange} placeholder="Select or type State" icon={FaMap} required readOnly={isExisting && !!fetchedData.state} disabled={false} />
              </div>

              <div className="protsahan-nidhi-donate-now-page-field">
                <label className="protsahan-nidhi-donate-now-page-label">District <span style={{ color: 'red' }}>*</span></label>
                <SearchableSelect name="district" value={formData.district} options={availableDistricts} onChange={handleChange} placeholder={formData.state ? "Select or type District" : "Please select State first"} icon={FaCity} required readOnly={isExisting && !!fetchedData.district} disabled={!formData.state} />
              </div>

              <div className="protsahan-nidhi-donate-now-page-field protsahan-nidhi-donate-now-page-full-width">
                <label className="protsahan-nidhi-donate-now-page-label">Current Address <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap protsahan-nidhi-donate-now-page-textarea-wrap">
                  <FaMapMarkerAlt className="protsahan-nidhi-donate-now-page-input-icon protsahan-nidhi-donate-now-page-textarea-icon" />
                  <textarea name="currentAddress" value={formData.currentAddress} onChange={handleCurrentAddressChange} placeholder="Enter current address" className="protsahan-nidhi-donate-now-page-textarea" rows="3" required readOnly={isExisting && !!fetchedData.currentAddress}></textarea>
                </div>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field protsahan-nidhi-donate-now-page-full-width protsahan-nidhi-donate-now-page-checkbox-field">
                <label className="protsahan-nidhi-donate-now-page-checkbox-label">
                  <input type="checkbox" name="isSameAddress" checked={formData.isSameAddress} onChange={handleSameAddressCheckboxChange} disabled={isExisting && !!fetchedData.permanentAddress} />
                  <span>Permanent address is same as Current address</span>
                </label>
              </div>

              <div className="protsahan-nidhi-donate-now-page-field protsahan-nidhi-donate-now-page-full-width">
                <label className="protsahan-nidhi-donate-now-page-label">Permanent Address <span style={{ color: 'red' }}>*</span></label>
                <div className="protsahan-nidhi-donate-now-page-input-wrap protsahan-nidhi-donate-now-page-textarea-wrap">
                  <FaMapMarkerAlt className="protsahan-nidhi-donate-now-page-input-icon protsahan-nidhi-donate-now-page-textarea-icon" />
                  <textarea name="permanentAddress" value={formData.permanentAddress} onChange={(e) => setFormData(prev => ({ ...prev, permanentAddress: e.target.value }))} placeholder={formData.isSameAddress ? "Same as Current Address" : "Enter permanent address"} className="protsahan-nidhi-donate-now-page-textarea" rows="3" required readOnly={formData.isSameAddress || (isExisting && !!fetchedData.permanentAddress)}></textarea>
                </div>
              </div>

              {/* Hindi Declaration Section Card Block - Shows ONLY if number is NOT verified / first time */}
              {!isExisting && (
                <div className="protsahan-nidhi-donate-now-page-field protsahan-nidhi-donate-now-page-full-width">
                  <div className="protsahan-nidhi-donate-now-page-declaration-box">
                    <h4 className="protsahan-nidhi-donate-now-page-dec-heading">घोषणा पत्र</h4>
                    <p className="protsahan-nidhi-donate-now-page-dec-text">
                      मैं घोषणा करता/करती हूँ कि राजपूत शिक्षा कोष द्वारा प्रायोजित राजपूत शिक्षा प्रोत्साहन निधि की स्वेच्छा से आजीवन सदस्यता ग्रहण कर रहा/रही हूँ जिसके तहत शुल्क 5,000 रुपये प्रतिवर्ष आर्थिक शिक्षा सहयोग देता रहूँगा/देती रहूँगी।
                    </p>
                    <div className="protsahan-nidhi-donate-now-page-dec-agreement">
                      <p className="protsahan-nidhi-donate-now-page-agree-notice-label">मैं उपरोक्त घोषणा से सहमत हूँ। <span style={{ color: 'red' }}>*</span></p>
                      <label className="protsahan-nidhi-donate-now-page-checkbox-holder">
                        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                        <span>हाँ, मैं सहमत हूँ।</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

            </div>
            <div className="protsahan-nidhi-donate-now-page-button-wrap">
              <button type="submit" className="protsahan-nidhi-donate-now-page-submit-btn"><FaDonate /> Submit Donation</button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default DonateNidhi;